/**
 * Parse a date-only "YYYY-MM-DD" string as LOCAL midnight,
 * avoiding UTC off-by-hours. Falls back to `new Date(dateStr)` for full timestamps.
 */
export function parseLocalDate(dateStr?: string | null): Date | null {
  if (!dateStr) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  return new Date(dateStr);
}

/**
 * Calculate the deadline for a sponsor activity.
 * Rule: activity_date + 1 calendar month (clamped to last day of target month).
 * Example: Jan 31 → Feb 28/29 (not Mar 3).
 * Returns start-of-day (used for display).
 */
export function getActivityDeadline(activityDate?: string | null): Date | null {
  if (!activityDate) return null;
  const d = parseLocalDate(activityDate);
  if (!d) return null;
  const day = d.getDate();
  d.setMonth(d.getMonth() + 1);
  // Handle month overflow (e.g., Jan 31 + 1 month = Feb 28/29, not Mar 3)
  if (d.getDate() < day) {
    d.setDate(0); // last day of target month
  }
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get deadline as a YYYY-MM-DD string (local time, avoids toISOString UTC off-by-one).
 */
export function getActivityDeadlineString(
  activityDate?: string | null,
): string | null {
  const d = getActivityDeadline(activityDate);
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Get the END of the deadline day: deadline date at 23:59:59.999 local.
 */
export function getDeadlineEnd(activityDate?: string | null): Date | null {
  const d = getActivityDeadline(activityDate);
  if (!d) return null;
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Get last day of cure period end: parseLocalDate(deadlineStr) + 7 days, set to 23:59:59.999 local.
 */
export function getLastCureDay(deadlineStr?: string | null): Date | null {
  if (!deadlineStr) return null;
  const deadlineDate = parseLocalDate(deadlineStr);
  if (!deadlineDate) return null;
  const d = new Date(deadlineDate.getTime() + 7 * 24 * 60 * 60 * 1000);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Check if current time is on or after the end of the cure period.
 */
export function isOnOrAfterEndOfCurePeriod(
  deadlineStr?: string | null,
): boolean {
  const lastCureDay = getLastCureDay(deadlineStr);
  if (!lastCureDay) return false;
  return new Date() >= lastCureDay;
}

/**
 * Sponsor activity date boundary rule.
 * Earliest selectable activity date: local midnight tomorrow.
 */
export function getEarliestSelectableDate(now: Date = new Date()): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
}

/**
 * Earliest selectable activity date as local "YYYY-MM-DD" string.
 */
export function getEarliestSelectableDateString(now: Date = new Date()): string {
  const d = getEarliestSelectableDate(now);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Whether an activity date is on or after tomorrow (strictly future, not today).
 */
export function isValidActivityDate(value?: string | null, now: Date = new Date()): boolean {
  const selected = parseLocalDate(value);
  if (!selected) return false;
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const selectedStart = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate());
  return selectedStart > todayStart;
}